import { useId } from "react";
import {
  CoveragePartSection,
  CoverageRows,
  TaxesAndFeesDetail,
} from "@/components/CoveragePartSection";
import { MarketBadge } from "@/components/CarrierIdentity";
import { MoneyText } from "@/components/MoneyText";
import {
  coveragePartsOf,
  partEconomicsRows,
  termsTotalsOf,
  taxesAndFeesFor,
  COVERAGE_LABELS,
} from "@/lib/submissions/coverageRows";
import type {
  BillingElection,
  CoverageType,
} from "@/lib/submissions/coverageTerms";
import {
  BILLING_TYPE_LABELS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_PLAN_LABELS,
} from "@/lib/submissions/quoteBinding";

/** Shared broker terms renderer for quotes and subsequent binder/policy views. */
export function CoverageTermsSummary({
  terms,
  compact = false,
  marketType,
  billing,
  coverageTypes = [],
}: {
  terms: unknown;
  compact?: boolean;
  marketType?: "admitted" | "surplus_lines" | null;
  billing?: BillingElection;
  coverageTypes?: CoverageType[];
}) {
  const id = useId();
  const parts = coveragePartsOf(terms);
  if (compact)
    return (
      <span className="flex min-w-0 flex-col gap-2 text-sm">
        {parts.map((p, i) => {
          const premium = partEconomicsRows(p).find(
            (row) => row.key === "grossPremium",
          );
          return (
            <span
              key={i}
              className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1"
            >
              <span>
                {p.label} <MarketBadge type={p.marketType ?? marketType} />
              </span>
              {premium && <MoneyText value={premium.value} />}
            </span>
          );
        })}
      </span>
    );
  const totals = termsTotalsOf(terms);
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {parts.map((p, i) => (
        <CoveragePartSection
          key={i}
          part={p}
          marketType={marketType}
          charges={taxesAndFeesFor(terms, p.coverageType)}
        />
      ))}
      {coverageTypes
        .filter((t) => !parts.some((p) => p.coverageType === t))
        .map((t) => (
          <section
            key={t}
            aria-label={COVERAGE_LABELS[t]}
            className="text-sm text-brk-dim"
          >
            <h4 className="text-subheading">{COVERAGE_LABELS[t]}</h4>
            <p>Not included in this offer</p>
          </section>
        ))}
      {totals.length > 0 && (
        <section aria-labelledby={id} className="flex flex-col gap-3 text-sm">
          <h4 id={id} className="text-subheading text-brk-text">
            Quote totals
          </h4>
          <CoverageRows rows={totals} />
          <TaxesAndFeesDetail charges={taxesAndFeesFor(terms, null)} />
        </section>
      )}
      {billing && (
        <dl className="flex flex-col gap-2 text-sm">
          {billing.billingType && (
            <div>
              <dt className="text-brk-dim">Billing type</dt>
              <dd>{BILLING_TYPE_LABELS[billing.billingType]}</dd>
            </div>
          )}
          {billing.paymentPlan && (
            <div>
              <dt className="text-brk-dim">Payment plan</dt>
              <dd>{PAYMENT_PLAN_LABELS[billing.paymentPlan]}</dd>
            </div>
          )}
          {billing.paymentMethod && (
            <div>
              <dt className="text-brk-dim">Payment method</dt>
              <dd>{PAYMENT_METHOD_LABELS[billing.paymentMethod]}</dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
}
